import { UseFormReturn } from "react-hook-form";
import { User, getAuth, sendPasswordResetEmail } from "firebase/auth";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";

import { Mail, UserRound, CalendarDays, Shield, RefreshCw } from "lucide-react";

interface ProfileUpdateFormProps {
    user: User | null;
    isLoadingProfile: boolean;
    form: UseFormReturn<{ displayName: string; birthDate: string; gender: string }>;
    handleProfileUpdate: (data: { displayName: string; birthDate: string; gender: string }) => void;
    handlePasswordResetRequest: (data: {
        oldPassword?: string;
        newPassword: string;
        confirmPassword: string;
    }) => Promise<void>;
    hasPassword: boolean;
}

export default function ProfileUpdateForm({
                                              user,
                                              isLoadingProfile,
                                              form,
                                              handleProfileUpdate,
                                              hasPassword,
                                          }: ProfileUpdateFormProps) {
    const {
        register,
        handleSubmit,
        formState: { isDirty, errors: profileErrors },
        setValue,
        watch,
    } = form;

    const auth = getAuth();
    const [isCooldown, setIsCooldown] = useState(false);
    const [cooldownTime, setCooldownTime] = useState(30);

    const handleSetPassword = async () => {
        if (isCooldown) return;

        if (user) {
            await sendPasswordResetEmail(auth, user.email!);
            toast.success("Письмо для установки пароля отправлено!");

            const endTime = Date.now() + 30000;
            localStorage.setItem("passwordResetCooldown", endTime.toString());

            setIsCooldown(true);
            setCooldownTime(30);
        }
    };

    const restoreCooldown = () => {
        const savedTime = localStorage.getItem("passwordResetCooldown");

        if (savedTime) {
            const timeLeft = Math.floor((parseInt(savedTime) - Date.now()) / 1000);
            if (timeLeft > 0) {
                setIsCooldown(true);
                setCooldownTime(timeLeft);
            } else {
                setIsCooldown(false);
                localStorage.removeItem("passwordResetCooldown");
            }
        }
    };

    useEffect(() => {
        restoreCooldown();

        const interval = setInterval(() => {
            const savedTime = localStorage.getItem("passwordResetCooldown");

            if (savedTime) {
                const timeLeft = Math.floor((parseInt(savedTime) - Date.now()) / 1000);
                if (timeLeft > 0) {
                    setCooldownTime(timeLeft);
                } else {
                    setIsCooldown(false);
                    setCooldownTime(0);
                    localStorage.removeItem("passwordResetCooldown");
                    clearInterval(interval);
                }
            }
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    const genderValue = watch("gender") || "";

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                    <div className="text-sm font-semibold">Обновление профиля</div>
                    <div className="text-xs text-muted-foreground">
                        Проверь данные. Изменения сохранятся сразу после обновления
                    </div>
                </div>

                <Badge variant="secondary" className="rounded-full">
                    <Shield className="mr-2 h-4 w-4" />
                    {hasPassword ? "Пароль подключен" : "Только соц вход"}
                </Badge>
            </div>

            <Separator />

            <form onSubmit={handleSubmit(handleProfileUpdate)} className="space-y-4">
                {/* Display name */}
                <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">ФИО</Label>
                    <div className="relative">
                        <UserRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            className="pl-10"
                            placeholder="Например: Айчолпон Ибраева"
                            {...register("displayName")}
                        />
                    </div>
                </div>

                {/* Email */}
                <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Email</Label>
                    <div className="relative">
                        <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input className="pl-10" value={user?.email || ""} disabled readOnly />
                    </div>
                </div>

                {/* Set password (only if social) */}
                {!hasPassword ? (
                    <div className="rounded-xl border bg-muted/20 p-3">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <div className="space-y-1">
                                <div className="text-sm font-medium">Установить пароль</div>
                                <div className="text-xs text-muted-foreground">
                                    Мы отправим письмо на твой email для установки пароля
                                </div>
                            </div>

                            <Button
                                type="button"
                                onClick={handleSetPassword}
                                disabled={isCooldown}
                                className="sm:min-w-[210px]"
                            >
                                {isCooldown ? (
                                    <>
                                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                        Подожди {cooldownTime} сек
                                    </>
                                ) : (
                                    <>
                                        <Shield className="mr-2 h-4 w-4" />
                                        Установить пароль
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                ) : null}

                {/* Birth date */}
                <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Дата рождения</Label>
                    <div className="relative">
                        <CalendarDays className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input className="pl-10" type="date" {...register("birthDate")} />
                    </div>
                </div>

                {/* Gender */}
                <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Пол</Label>

                    <Select
                        value={genderValue}
                        onValueChange={(v) => setValue("gender", v, { shouldDirty: true, shouldValidate: true })}
                    >
                        <SelectTrigger
                            className={[
                                profileErrors.gender ? "border-destructive focus:ring-destructive" : "",
                            ].join(" ")}
                        >
                            <SelectValue placeholder="Выберите пол" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="Мужчина">Мужской</SelectItem>
                            <SelectItem value="Женщина">Женский</SelectItem>
                        </SelectContent>
                    </Select>

                    {profileErrors.gender?.message ? (
                        <p className="text-xs text-destructive">{profileErrors.gender.message as string}</p>
                    ) : null}
                </div>

                {/* Submit */}
                <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:justify-end">
                    <Button type="submit" disabled={!isDirty || isLoadingProfile} className="sm:min-w-[220px]">
                        {isLoadingProfile ? (
                            <>
                                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                Обновляем
                            </>
                        ) : (
                            "Обновить профиль"
                        )}
                    </Button>
                </div>
            </form>
        </div>
    );
}
